import { z } from 'zod';

/**
 * FieldSpec — the typed contract a component publishes. The Inspector
 * generates its form from these; the AI composer gets them as the menu;
 * `resolveSection` uses them to resolve values/bindings into props.
 */

const base = { name: z.string().min(1), label: z.string().min(1) };

export const textFieldSchema = z.object({
  kind: z.literal('text'),
  ...base,
  default: z.string().optional(),
  /** render a textarea instead of a single-line input */
  multiline: z.boolean().optional(),
});
export const richtextFieldSchema = z.object({
  kind: z.literal('richtext'),
  ...base,
  default: z.string().optional(),
});
export const numberFieldSchema = z.object({
  kind: z.literal('number'),
  ...base,
  default: z.number().optional(),
});
export const imageFieldSchema = z.object({
  kind: z.literal('image'),
  ...base,
  default: z.string().optional(),
});
export const selectFieldSchema = z.object({
  kind: z.literal('select'),
  ...base,
  options: z.array(z.string().min(1)).min(1),
  default: z.string().optional(),
});
export const bindingFieldSchema = z.object({
  kind: z.literal('binding'),
  ...base,
  format: z.enum(['currency', 'number', 'date']).optional(),
});

export type ListFieldSpec = {
  kind: 'list';
  name: string;
  label: string;
  item: FieldSpec[];
};

export type FieldSpec =
  | z.infer<typeof textFieldSchema>
  | z.infer<typeof richtextFieldSchema>
  | z.infer<typeof numberFieldSchema>
  | z.infer<typeof imageFieldSchema>
  | z.infer<typeof selectFieldSchema>
  | z.infer<typeof bindingFieldSchema>
  | ListFieldSpec;

export const fieldSpecSchema: z.ZodType<FieldSpec> = z.lazy(() =>
  z.union([
    textFieldSchema,
    richtextFieldSchema,
    numberFieldSchema,
    imageFieldSchema,
    selectFieldSchema,
    bindingFieldSchema,
    z.object({
      kind: z.literal('list'),
      ...base,
      item: z.array(fieldSpecSchema),
    }),
  ]),
);
